export type VisualisationValue =
  | 'trend-line-chart'
  | 'bar-chart'
  | 'metrics-card'
  | 'pie-chart'
  | 'heatmap-chart'
  | 'distribution-chart'
  | 'improvement-chart'
  | 'decline-chart'
  | 'text';

type Option = {
  label: string;
  value: string;
};

type VisualisationField = {
  value: string | number;
  label: string;
  placeholder: string;
  options: Option[];
  optional?: boolean;
  clearable?: boolean;
};

export type DimensionValue = {
  label: string;
  value: VisualisationValue;
};

export type VisualisationOption = {
  value: VisualisationValue;
  label: string;
  options?: VisualisationField[];
};

export const DEFAULT_DIMENSION_VALUE: DimensionValue = {
  label: 'Trend line chart',
  value: 'trend-line-chart',
};

export const VISUALISATION_OPIONS: VisualisationOption[] = [
  {
    value: 'trend-line-chart',
    label: 'Trend line chart',
    options: [
      {
        value: 'category',
        label: 'Category',
        clearable: true,
        placeholder: 'Select category',
        options: [
          {
            label: 'Job satisfaction',
            value: 'job-satisfaction',
          },
          {
            label: 'Team Dynamics',
            value: 'team-dynamics',
          },
        ],
      },
      {
        value: 'question',
        label: 'Question',
        optional: true,
        placeholder: 'Select question',
        options: [
          {
            label: 'Question 1',
            value: 'question1',
          },
          {
            label: 'Question 2',
            value: 'question2',
          },
        ],
      },
      {
        value: 'group_by',
        label: 'Group By',
        placeholder: 'Group By',
        options: [
          { label: 'By department', value: 'department' },
          { label: 'By leader', value: 'leader' },
          { label: 'By gender', value: 'gender' },
          { label: 'By location', value: 'location' },
          { label: 'By tenure', value: 'tenure' },
        ],
      },
    ],
  },
  {
    value: 'bar-chart',
    label: 'Bar Chart',
    options: [
      {
        value: 'data-dimension',
        label: 'Data Dimension',
        placeholder: 'Select dimension',
        options: [
          { label: 'Avg. Employee count', value: 'avg-employee-count' },
          {
            label: 'Response count',
            value: 'response-count',
          },
          {
            label: 'Participation rate',
            value: 'participation-rate',
          },
          {
            label: 'Average score',
            value: 'average-score',
          },
          {
            label: 'eNPS',
            value: 'enps',
          },
        ],
      },
      {
        value: 'grouping',
        label: 'Grouping',
        placeholder: 'Select grouping',
        options: [
          { label: 'By department', value: 'department' },
          { label: 'By leader', value: 'leader' },
          { label: 'By gender', value: 'gender' },
          { label: 'By location', value: 'location' },
          { label: 'By tenure', value: 'tenure' },
        ],
      },
    ],
  },
  {
    value: 'metrics-card',
    label: 'Metrics card',
    options: [
      {
        value: 'data-dimension',
        label: 'Data Dimension',
        clearable: true,
        placeholder: 'Select dimension',
        options: [
          { label: 'Avg. Employee count', value: 'avg-employee-count' },
          {
            label: 'Response count',
            value: 'response-count',
          },
          {
            label: 'Participation rate',
            value: 'participation-rate',
          },
          {
            label: 'Average score',
            value: 'average-score',
          },
          {
            label: 'eNPS',
            value: 'enps',
          },
        ],
      },
      {
        value: 'grouping',
        label: 'Grouping',
        clearable: true,
        placeholder: 'Select grouping',
        options: [
          { label: 'By department', value: 'department' },
          { label: 'By leader', value: 'leader' },
          { label: 'By gender', value: 'gender' },
          { label: 'By location', value: 'location' },
          { label: 'By tenure', value: 'tenure' },
        ],
      },
    ],
  },
  {
    value: 'pie-chart',
    label: 'Pie chart',
    options: [
      {
        value: 'data-dimension',
        label: 'Data Dimension',
        clearable: true,
        placeholder: 'Select dimension',
        options: [
          { label: 'Work-Life Balance score', value: 'work-life-balance-score' },
          { label: 'Avg. Employee count', value: 'avg-employee-count' },
        ],
      },
      {
        value: 'grouping',
        label: 'Grouping',
        clearable: true,
        placeholder: 'Select grouping',
        options: [
          { label: 'By department', value: 'department' },
          { label: 'By leader', value: 'leader' },
          { label: 'By gender', value: 'gender' },
          { label: 'By location', value: 'location' },
          { label: 'By tenure', value: 'tenure' },
        ],
      },
    ],
  },
  {
    value: 'heatmap-chart',
    label: 'Heatmap chart',
    options: [
      {
        value: 'row-dimension',
        label: 'Row Dimension',
        placeholder: 'Select row dimension',
        options: [{ label: 'Question categories', value: 'question-categories' }],
      },
      {
        value: 'column-dimension',
        label: 'Column Dimension',
        placeholder: 'Select column dimension',
        options: [{ label: 'Departments', value: 'departments' }],
      },
      {
        value: 'color-scale',
        label: 'Color scale',
        placeholder: 'Select color scale',
        options: [{ label: 'Gradient', value: 'departments' }],
      },
    ],
  },
  {
    value: 'distribution-chart',
    label: 'Distribution Chart',
    options: [
      {
        value: 'question',
        label: 'Select question',
        placeholder: 'Select question',
        options: [
          { label: 'I feel valued at work', value: 'valued-at-work' },
          { label: 'Stress levels in my job are manageable', value: 'stress-levels' },
          {
            label: 'I can disconnect from work during my personal time',
            value: 'disconnect-from-work',
          },
        ],
      },
      {
        value: 'grouping',
        label: 'Grouping',
        placeholder: 'Select grouping',
        options: [
          { label: 'By department', value: 'department' },
          { label: 'By leader', value: 'leader' },
          { label: 'By gender', value: 'gender' },
          { label: 'By location', value: 'location' },
          { label: 'By tenure', value: 'tenure' },
        ],
      },
    ],
  },
  {
    value: 'improvement-chart',
    label: 'Improvement chart',
    options: [
      {
        value: 'timeframes',
        label: 'Comparison timeframes',
        placeholder: 'Select comparison timeframes',
        options: [
          { label: 'Previous week', value: 'week' },
          { label: 'Previous month', value: 'month' },
          { label: 'Previous quarter', value: 'quarter' },
          { label: 'Previous year', value: 'year' },
        ],
      },
      {
        value: 'number-of-items',
        label: 'Number of items',
        placeholder: 'Select number of items',
        options: [
          { label: '3', value: '3' },
          { label: '4', value: '4' },
          { label: '5', value: '5' },
          { label: '6', value: '6' },
        ],
      },
    ],
  },
  {
    value: 'decline-chart',
    label: 'Decline chart',
    options: [
      {
        value: 'timeframes',
        label: 'Comparison timeframes',
        placeholder: 'Select comparison timeframes',
        options: [
          { label: 'Previous week', value: 'week' },
          { label: 'Previous month', value: 'month' },
          { label: 'Previous quarter', value: 'quarter' },
          { label: 'Previous year', value: 'year' },
        ],
      },
      {
        value: 'number-of-items',
        label: 'Number of items',
        placeholder: 'Select number of items',
        options: [
          { label: '3', value: '3' },
          { label: '4', value: '4' },
          { label: '5', value: '5' },
          { label: '6', value: '6' },
        ],
      },
    ],
  },
  {
    value: 'text',
    label: 'Text',
    options: [
      {
        value: 'type',
        label: 'Type',
        placeholder: 'Select type',
        options: [
          { label: 'Title, Body text', value: 'title-body' },
          { label: 'Title', value: 'title' },
          { label: 'Body text', value: 'body' },
        ],
      },
    ],
  },
];
